// Pure date helpers for personal reminders. All conversions are local-time on
// purpose (the app runs in the user's timezone, RO = UTC+2/+3): a reminder set
// for "mâine la 09:00" must mean the user's 09:00, not UTC's.

import { toDateKey } from './calendarDates';

/** Local `YYYY-MM-DD` + `HH:mm` → ISO instant (what the API stores). */
export function combineDateTime(dateKey: string, time: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  return new Date(y, m - 1, d, hh, mm, 0, 0).toISOString();
}

/** ISO instant → local `{ date: 'YYYY-MM-DD', time: 'HH:mm' }` form values. */
export function splitDueAt(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return { date: toDateKey(d), time: `${hh}:${mm}` };
}

/**
 * Compact due chip: "Astăzi, 09:00" / "Mâine, 09:00" / "8 sept., 09:00".
 * The today/tomorrow words come from the caller's i18n so this stays pure.
 */
export function formatDueChip(
  iso: string,
  locale: string,
  labels: { today: string; tomorrow: string },
): string {
  const due = new Date(iso);
  const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(due);
  const now = new Date();
  const dueKey = toDateKey(due);
  if (dueKey === toDateKey(now)) return `${labels.today}, ${time}`;
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  if (dueKey === toDateKey(tomorrow)) return `${labels.tomorrow}, ${time}`;
  const day = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(due);
  return `${day}, ${time}`;
}

/** A reminder fires this long before its due moment (the red banner, the calendar chip). */
export const REMINDER_FIRE_BEFORE_MS = 10 * 60_000;

/**
 * Fired = open, not dismissed, and within 10 minutes of (or past) its due
 * time. One rule for the banner and the calendar: a reminder shows up in both
 * at the same moment and leaves both when the user acts on it — Finalizat,
 * Amână (a new dueAt re-arms it server-side) or X (ack for this firing).
 */
export function isReminderFiring(
  reminder: { done: boolean; ackAt: string | null; dueAt: string },
  nowMs: number,
): boolean {
  return (
    !reminder.done &&
    reminder.ackAt === null &&
    Date.parse(reminder.dueAt) - REMINDER_FIRE_BEFORE_MS <= nowMs
  );
}

/** Compact duration estimate: "30 min" / "1h" / "1h 30m". */
export function formatEstimate(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** Snooze target: `nowMs + n` minutes (server-corrected now, not old dueAt). */
export function plusMinutes(nowMs: number, minutes: number): string {
  return new Date(nowMs + minutes * 60_000).toISOString();
}

/** Snooze target: tomorrow at 09:00 local time. */
export function tomorrowAt9(nowMs: number): string {
  const now = new Date(nowMs);
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 9, 0, 0, 0).toISOString();
}
